import { useMemo } from 'react';

export const useSaveButtonState = <T extends Record<string, unknown>>(
  originalData: T[],
  data: T[],
  compareFields: (keyof T)[],
) => {
  return useMemo(() => {
    if (originalData.length !== data.length) return false;

    return originalData.every((orig, index) => {
      const current = data[index];
      if (!current) return false;

      return compareFields.every((field) => orig[field] === current[field]);
    });
  }, [originalData, data, compareFields]);
};

export const useNationalProductsSaveState = <
  T extends { priceLock?: boolean; mandatoryProductLock?: boolean; isVisible?: boolean; variantId?: string },
>(
  originalData: T[],
  data: T[],
) => {
  return useMemo(() => {
    // Fast path: early exit if lengths differ
    if (originalData.length !== data.length) return false;

    // Fast path: early exit if arrays are the same reference
    if (originalData === data) return true;

    // Optimize for large datasets: use Map for O(1) lookups instead of index-based comparison
    // This handles cases where items might be in different order
    if (originalData.length > 50) {
      const originalMap = new Map(originalData.map((item) => [item.variantId || '', item]));

      for (const current of data) {
        const id = current.variantId || '';
        const original = originalMap.get(id);

        if (!original) return false;

        // Compare only the fields we care about
        if (
          Boolean(original.priceLock) !== Boolean(current.priceLock) ||
          Boolean(original.mandatoryProductLock) !== Boolean(current.mandatoryProductLock) ||
          Boolean(original.isVisible) !== Boolean(current.isVisible)
        ) {
          return false;
        }
      }

      return true;
    }

    // For smaller datasets, use index-based comparison (assumes same order)
    return originalData.every((orig, index) => {
      const current = data[index];
      if (!current) return false;

      return (
        Boolean(orig.priceLock) === Boolean(current.priceLock) &&
        Boolean(orig.mandatoryProductLock) === Boolean(current.mandatoryProductLock) &&
        Boolean(orig.isVisible) === Boolean(current.isVisible)
      );
    });
  }, [originalData, data]);
};

export const useNationalModifiersSaveState = <
  T extends {
    id?: string;
    isLock?: boolean | string;
    isVisible?: boolean | string;
    isMandatory?: boolean | string;
    isZeroPriced?: boolean | string;
  },
>(
  originalData: T[],
  data: T[],
) => {
  return useMemo(() => {
    if (originalData.length !== data.length) return false;
    if (originalData === data) return true;

    const toBool = (v: boolean | string | undefined) => (v === 'false' ? false : Boolean(v));

    if (originalData.length > 50) {
      const originalMap = new Map(originalData.map((item) => [item.id || '', item]));
      for (const current of data) {
        const original = originalMap.get(current.id || '');
        if (!original) return false;

        if (
          toBool(original.isLock) !== toBool(current.isLock) ||
          toBool(original.isVisible) !== toBool(current.isVisible) ||
          toBool(original.isMandatory) !== toBool(current.isMandatory) ||
          toBool(original.isZeroPriced) !== toBool(current.isZeroPriced)
        ) {
          return false;
        }
      }
      return true;
    }

    return originalData.every((orig, index) => {
      const current = data[index];
      if (!current) return false;

      return (
        toBool(orig.isLock) === toBool(current.isLock) &&
        toBool(orig.isVisible) === toBool(current.isVisible) &&
        toBool(orig.isMandatory) === toBool(current.isMandatory) &&
        toBool(orig.isZeroPriced) === toBool(current.isZeroPriced)
      );
    });
  }, [originalData, data]);
};
